import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CreateEventInput, Event, UpdateEventInput } from '@nx/domain';

export interface EventsState {
  items: Event[];
  current: Event | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: EventsState = {
  items: [],
  current: null,
  loading: false,
  saving: false,
  error: null,
};

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    eventsWatchRequested(_state, _action: PayloadAction<string>) {},
    eventsLoaded(state, action: PayloadAction<Event[]>) {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    eventLoadRequested(state, _action: PayloadAction<string>) {
      state.loading = true;
      state.error = null;
    },
    eventLoaded(state, action: PayloadAction<Event | null>) {
      state.current = action.payload;
      state.loading = false;
    },
    eventCreateRequested(
      state,
      _action: PayloadAction<{ input: CreateEventInput; coverFile?: File | null }>,
    ) {
      state.saving = true;
      state.error = null;
    },
    eventUpdateRequested(
      state,
      _action: PayloadAction<{
        eventId: string;
        input: UpdateEventInput;
        coverFile?: File | null;
      }>,
    ) {
      state.saving = true;
      state.error = null;
    },
    eventSaveSucceeded(state, action: PayloadAction<Event>) {
      state.saving = false;
      state.current = action.payload;
      const idx = state.items.findIndex((e) => e.id === action.payload.id);
      if (idx >= 0) state.items[idx] = action.payload;
      else state.items.unshift(action.payload);
    },
    eventDeleteRequested(_state, _action: PayloadAction<string>) {},
    eventDeleted(state, action: PayloadAction<string>) {
      state.items = state.items.filter((e) => e.id !== action.payload);
      if (state.current?.id === action.payload) state.current = null;
    },
    eventsFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const {
  eventsWatchRequested,
  eventsLoaded,
  eventLoadRequested,
  eventLoaded,
  eventCreateRequested,
  eventUpdateRequested,
  eventSaveSucceeded,
  eventDeleteRequested,
  eventDeleted,
  eventsFailed,
} = eventsSlice.actions;

export default eventsSlice.reducer;
